import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { AVATARS } from "@/data/exercises";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Users, Plus, Play, Pencil, Trash2, Check, Activity, Clock, Loader2, LineChart as LineIcon, FileText } from "lucide-react";

function GroupDialog({ open, onOpenChange, patients, editing, onSaved }) {
  const [name, setName] = useState("");
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(editing?.name || "");
      setMembers(editing?.member_ids || []);
    }
  }, [open, editing]);

  const toggle = (id) => setMembers((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));

  const save = async () => {
    if (!name.trim()) return toast.error("Donnez un nom au groupe.");
    setSaving(true);
    try {
      if (editing) await api.put(`/groups/${editing.id}`, { name, member_ids: members });
      else await api.post("/groups", { name, member_ids: members });
      toast.success(editing ? "Groupe mis à jour" : "Groupe créé");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="group-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Modifier le groupe" : "Nouveau groupe"}</DialogTitle>
          <DialogDescription>Nommez le groupe et choisissez les résidents (modifiable à tout moment).</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Nom du groupe</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Ex : Étage 1, Groupe Mémoire, Atelier du mardi" className="mt-1" data-testid="group-name-input" />
          </div>
          <div>
            <Label>Résidents du groupe</Label>
            {patients.length === 0 ? (
              <p className="text-sm text-gray-400 mt-2">Aucun résident. Créez d'abord des profils.</p>
            ) : (
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
                {patients.map((p) => {
                  const on = members.includes(p.id);
                  return (
                    <button key={p.id} type="button" onClick={() => toggle(p.id)} data-testid={`group-member-toggle-${p.id}`}
                      className={`flex items-center gap-3 rounded-xl border-2 p-2 text-left transition-colors ${on ? "bg-emerald-50 border-emerald-500" : "bg-white border-gray-200"}`}>
                      <img src={p.avatar || AVATARS[0]} alt={p.name} className="w-10 h-10 rounded-full object-cover border-2 border-amber-200" />
                      <span className="flex-1 font-medium text-gray-800 truncate">{p.name}</span>
                      {on && <Check className="w-5 h-5 text-emerald-600" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-group-button">
            {saving ? "Enregistrement..." : editing ? "Enregistrer" : "Créer le groupe"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GroupSuivi({ group, open, onOpenChange }) {
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    if (open) {
      api.get(`/groups/${group.id}/stats`).then((r) => setStats(r.data)).catch(() => setStats(null));
      api.get(`/groups/${group.id}/sessions`).then((r) => setSessions(r.data)).catch(() => setSessions([]));
    }
  }, [open, group.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="group-suivi-dialog">
        <DialogHeader>
          <DialogTitle>Suivi du groupe — {group.name}</DialogTitle>
          <DialogDescription>Séances de groupe réalisées et participation des résidents.</DialogDescription>
        </DialogHeader>
        <div className="flex justify-end -mt-2">
          <Button variant="outline" size="sm" className="rounded-lg" data-testid="download-group-report"
            onClick={async () => {
              try {
                const res = await api.get(`/groups/${group.id}/report/pdf`, { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url; a.download = `suivi-groupe-${group.name}.pdf`; a.click();
                URL.revokeObjectURL(url);
                toast.success("Suivi PDF téléchargé");
              } catch { toast.error("Téléchargement impossible."); }
            }}>
            <FileText className="w-4 h-4 mr-1" /> Télécharger le PDF
          </Button>
        </div>
        {!stats ? (
          <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-emerald-700" /></div>
        ) : (
          <div className="space-y-5 py-2">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-emerald-50 p-4 text-center"><p className="text-2xl font-bold text-emerald-800" data-testid="suivi-total-sessions">{stats.total_sessions}</p><p className="text-xs text-gray-500">Séances</p></div>
              <div className="rounded-xl bg-amber-50 p-4 text-center"><p className="text-2xl font-bold text-amber-700">{stats.avg_score}%</p><p className="text-xs text-gray-500">Réussite moy.</p></div>
              <div className="rounded-xl bg-emerald-50 p-4 text-center"><p className="text-2xl font-bold text-emerald-800">{stats.total_minutes}</p><p className="text-xs text-gray-500">Minutes</p></div>
            </div>

            {stats.per_member.length > 0 && (
              <div>
                <p className="font-semibold text-gray-700 mb-2">Participation par résident</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stats.per_member} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="name" stroke="#9ca3af" />
                    <YAxis allowDecimals={false} stroke="#9ca3af" />
                    <Tooltip />
                    <Bar dataKey="sessions" name="Séances" fill="#23739F" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            <div>
              <p className="font-semibold text-gray-700 mb-2">Dernières séances</p>
              {sessions.length === 0 ? (
                <p className="text-gray-400 py-6 text-center text-sm">Aucune séance de groupe pour l'instant.</p>
              ) : (
                <div className="space-y-2" data-testid="group-sessions-list">
                  {sessions.map((s) => (
                    <div key={s.id} className="rounded-xl border border-gray-200 p-3">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-800">{s.exercise_name || "Activité"}</span>
                        <span className="text-xs text-gray-400">{s.completed_at?.slice(0, 10)}</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{(s.participant_names || []).join(", ")} · Réussite {s.score}%</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function GroupsManager({ patients }) {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [suivi, setSuivi] = useState(null);

  const load = useCallback(() => {
    api.get("/groups").then((r) => setGroups(r.data)).catch(() => setGroups([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try { await api.delete(`/groups/${id}`); toast.success("Groupe supprimé"); load(); }
    catch { toast.error("Suppression impossible."); }
  };

  return (
    <div data-testid="groups-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif-display text-3xl font-normal text-gray-800">Mes groupes</h1>
          <p className="text-gray-500 mt-1">Créez des groupes de résidents et lancez des séances partagées.</p>
        </div>
        <Button className="bg-emerald-700 hover:bg-emerald-800 rounded-xl" data-testid="add-group-button"
          onClick={() => { setEditing(null); setDialogOpen(true); }}>
          <Plus className="w-4 h-4 mr-1" /> Nouveau groupe
        </Button>
      </div>

      {loading ? (
        <p className="text-gray-400">Chargement...</p>
      ) : groups.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300 bg-transparent">
          <CardContent className="p-14 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4"><Users className="w-8 h-8 text-emerald-700" /></div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Aucun groupe pour l'instant</h3>
            <p className="text-gray-500 max-w-sm">Regroupez vos résidents (ex. « Étage 1 ») pour animer des activités en groupe avec un suivi dédié.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="groups-grid">
          {groups.map((g) => (
            <Card key={g.id} className="border-gray-200" data-testid={`group-card-${g.id}`}>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><Users className="w-6 h-6" /></span>
                  <div className="min-w-0">
                    <h3 className="font-bold text-lg text-gray-800 truncate">{g.name}</h3>
                    <p className="text-sm text-gray-500">{g.members.length} résident(s)</p>
                  </div>
                </div>
                <div className="flex -space-x-2 mb-5 min-h-[2rem]">
                  {g.members.slice(0, 6).map((m) => (
                    <img key={m.id} src={m.avatar || AVATARS[0]} alt={m.name} title={m.name}
                      className="w-9 h-9 rounded-full object-cover border-2 border-white" />
                  ))}
                  {g.members.length === 0 && <span className="text-sm text-gray-400">Aucun résident</span>}
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => navigate(`/groupe/${g.id}`)} disabled={g.members.length === 0}
                    className="flex-1 bg-emerald-700 hover:bg-emerald-800 rounded-xl" data-testid={`launch-group-${g.id}`}>
                    <Play className="w-4 h-4 mr-1" /> Séance
                  </Button>
                  <Button variant="outline" className="rounded-xl" onClick={() => setSuivi(g)} data-testid={`group-suivi-${g.id}`}>
                    <Activity className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" className="rounded-xl" onClick={() => { setEditing(g); setDialogOpen(true); }} data-testid={`edit-group-${g.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" className="rounded-xl text-red-600" data-testid={`delete-group-${g.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer « {g.name} » ?</AlertDialogTitle>
                        <AlertDialogDescription>Le groupe et son suivi de séances seront supprimés. Les résidents et leur suivi individuel sont conservés.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(g.id)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-group-${g.id}`}>Supprimer</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <GroupDialog open={dialogOpen} onOpenChange={setDialogOpen} patients={patients} editing={editing} onSaved={load} />
      {suivi && <GroupSuivi group={suivi} open={!!suivi} onOpenChange={(v) => !v && setSuivi(null)} />}
    </div>
  );
}
